// Provider shell — matches the patient/client portal exactly: full-width dark top bar (pt-topbar) +
// cream sidebar nav (pt-sidebar) + off-canvas mobile drawer. Same chrome across portals.
import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Brand, ThemeToggle, Modal, Avatar, Sidebar, useNavCollapse } from "@championcare/ui";
import { useAppAuth } from "../auth";
import { useApi } from "../api";
import { useProfileExtrasApi } from "../lib/profileExtrasApi";
import { useUnreadApi } from "../lib/unreadApi";
import { useIdleLogout } from "../lib/useIdleLogout";

const icons: Record<string, ReactNode> = {
  queue: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
  ),
  schedule: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
  ),
  patients: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
  ),
  messages: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
  ),
  availability: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><path d="M9 16l2 2 4-4" /></svg>
  ),
  profile: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 22v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" /></svg>
  ),
  signout: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
  ),
};

type NavItem = { to: string; label: string; icon: ReactNode; end?: boolean };

const GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: "Clinical",
    items: [
      { to: "/", label: "Schedule", icon: icons.schedule, end: true },
      { to: "/patients", label: "Patients", icon: icons.patients },
      { to: "/messages", label: "Messages", icon: icons.messages },
      { to: "/availability", label: "Availability", icon: icons.availability },
    ],
  },
  {
    label: "Account",
    items: [{ to: "/profile", label: "My profile", icon: icons.profile }],
  },
];

export function Shell() {
  const { displayName, signOut, isSignedIn } = useAppAuth();
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  // Top-bar identity comes from the provider's own profile ("Dr. First Last"), not the auth
  // displayName — which falls back to the raw account email under Cognito.
  const [profile, setProfile] = useState<{ first_name?: string | null; last_name?: string | null } | null>(null);
  useEffect(() => {
    let active = true;
    api.me().then((m) => active && setProfile(m)).catch(() => {});
    return () => {
      active = false;
    };
  }, [api]);
  const fullName = `${profile?.first_name ?? ""} ${profile?.last_name ?? ""}`.trim();
  const name = fullName ? `Dr. ${fullName}` : (displayName ?? "Provider");
  const initial = (() => {
    const parts = fullName.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? "";
    const last = parts.length >= 2 ? (parts[parts.length - 1]?.[0] ?? "") : "";
    return (first + last).toUpperCase() || name.trim().charAt(0).toUpperCase() || "D";
  })();
  const [navOpen, setNavOpen] = useState(false);
  const [confirmOut, setConfirmOut] = useState(false);
  const { collapsed, setCollapsed, toggle } = useNavCollapse("cc-nav-collapsed-doctor");
  // The provider's own photo for the top-bar chip (additive field via the thin profile-extras client).
  const extrasApi = useProfileExtrasApi();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    extrasApi
      .get()
      .then((x) => active && setAvatarUrl(x.avatar_url))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [extrasApi]);

  // Unread-message count for the Messages nav badge. Refetch on route change (so opening a thread,
  // which marks it read, drops the count) + poll every 5s to catch new inbound while navigating.
  const unreadApi = useUnreadApi();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let active = true;
    const load = () => unreadApi.count().then((c) => active && setUnread(c)).catch(() => {});
    load();
    const iv = setInterval(load, 5000);
    return () => {
      active = false;
      clearInterval(iv);
    };
  }, [unreadApi, location.pathname]);

  useIdleLogout(() => {
    void signOut();
    navigate("/sign-in");
  }, isSignedIn);

  useEffect(() => {
    document.body.classList.toggle("pt-nav-open", navOpen);
    return () => document.body.classList.remove("pt-nav-open");
  }, [navOpen]);

  const closeNav = () => setNavOpen(false);
  function handleSignOut() {
    closeNav();
    void signOut();
    navigate("/sign-in");
  }

  return (
    <div className="pt-root">
      <header className="pt-topbar">
        <Link to="/" className="pt-brand-link" aria-label="Champion Care — schedule">
          <Brand onDark size={22} />
        </Link>
        <div className="pt-topbar__spacer" />
        <div className="pt-topbar__actions">
          <ThemeToggle />
          <span className="pt-userchip">
            <Avatar className="pt-userchip__avatar" src={avatarUrl} initials={initial} />
            <span className="pt-userchip__name">{name}</span>
          </span>
          <button
            className="pt-navtoggle"
            onClick={() => setNavOpen((o) => !o)}
            aria-label="Menu"
            aria-expanded={navOpen}
          >
            <svg className="burger" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="7" x2="21" y2="7" /><line x1="3" y1="17" x2="21" y2="17" /></svg>
            <svg className="x" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18" /><line x1="6" y1="18" x2="18" y2="6" /></svg>
          </button>
        </div>
      </header>

      <div className={`pt-shell ${collapsed ? "nav-collapsed" : ""}`}>
        <div className="pt-nav-backdrop" onClick={closeNav} aria-hidden="true" />
        <Sidebar collapsed={collapsed} onToggle={toggle} onExpand={() => setCollapsed(false)}>
          {GROUPS.map((group) => (
            <div key={group.label} style={{ display: "contents" }}>
              <div className="pt-navlabel">{group.label}</div>
              {group.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={closeNav}
                  className={({ isActive }) => `pt-navlink ${isActive ? "is-active" : ""}`}
                >
                  {n.icon}
                  <span className="pt-navlink__label">{n.label}</span>
                  {n.to === "/messages" && unread > 0 && (
                    <span className="pt-navlink__badge">{unread > 99 ? "99+" : unread}</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
          <button className="pt-navlink pt-navlink--signout" onClick={() => setConfirmOut(true)}>
            {icons.signout}
            <span className="pt-navlink__label">Sign out</span>
          </button>
        </Sidebar>

        <main className="pt-main">
          <div key={location.pathname} className="page-fade">
            <Outlet />
          </div>
        </main>
      </div>

      <Modal open={confirmOut} onClose={() => setConfirmOut(false)} labelledBy="signout-title">
        <div className="dialog-eyebrow">Sign out</div>
        <div className="dialog-title" id="signout-title">Sign out of <em>Champion Care?</em></div>
        <div className="dialog-body">You&rsquo;ll need to sign in again to access your schedule, patients, and messages.</div>
        <div className="dialog-actions">
          <button className="btn btn-ghost" onClick={() => setConfirmOut(false)}>Stay signed in</button>
          <button className="btn btn-primary" onClick={handleSignOut}>Sign out</button>
        </div>
      </Modal>
    </div>
  );
}
